import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  AppSvgIconComponent,
  SignalButton,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { PageHeaderComponent } from '@core/components/page-header/page-header.component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type {
  FormListItemResponse,
  FormListItemResponseStatus,
  ListFormsApiResponse,
} from '@core/api/workspace/models';
import { FormListItemResponseStatus as FormStatusEnum } from '@core/api/workspace/models';
import { CreateFormFlowService } from '../../services/create-form-flow.service';

type FormStatusFilter = 'all' | FormListItemResponseStatus;

@Component({
  selector: 'app-forms-list-page',
  imports: [
    DatePipe,
    PageHeaderComponent,
    SignalButton,
    SignalTextField,
    AppSvgIconComponent,
  ],
  templateUrl: './forms-list-page.html',
  styleUrl: './forms-list-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormsListPage extends BaseComponent implements OnInit {
  private readonly createFormFlow = inject(CreateFormFlowService);
  private readonly userAPIService = inject(WorkspaceAPIService);

  readonly publishedStatus = FormStatusEnum.published;
  readonly listState = new State<ListFormsApiResponse>();
  readonly forms = signal<FormListItemResponse[]>([]);
  readonly selectedStatus = signal<FormStatusFilter>('all');
  readonly searchText = signal('');

  readonly statusFilters: Array<{ label: string; value: FormStatusFilter }> = [
    { label: 'All', value: 'all' },
    { label: 'Published', value: FormStatusEnum.published },
    { label: 'Draft', value: FormStatusEnum.draft },
    { label: 'Unpublished', value: FormStatusEnum.unpublished },
    { label: 'Finished', value: FormStatusEnum.finished },
  ];

  readonly filteredForms = computed(() => {
    const status = this.selectedStatus();
    const search = this.searchText().toLowerCase().trim();

    return this.forms().filter((form) => {
      const matchesStatus = status === 'all' || form.status === status;
      const description = form.description?.toLowerCase() ?? '';
      const matchesSearch =
        !search ||
        form.name.toLowerCase().includes(search) ||
        description.includes(search) ||
        form.type.toLowerCase().includes(search);

      return matchesStatus && matchesSearch;
    });
  });

  readonly totalForms = computed(() => this.forms().length);
  readonly publishedForms = computed(
    () => this.forms().filter((x) => x.status === FormStatusEnum.published).length,
  );
  readonly draftForms = computed(
    () => this.forms().filter((x) => x.status === FormStatusEnum.draft).length,
  );
  readonly unpublishedForms = computed(
    () =>
      this.forms().filter((x) => x.status === FormStatusEnum.unpublished).length,
  );

  ngOnInit(): void {
    this.loadForms();
  }

  setStatus(status: FormStatusFilter): void {
    this.selectedStatus.set(status);
  }

  onSearchValueChange(value: unknown): void {
    this.searchText.set(typeof value === 'string' ? value : '');
  }

  createNewForm(): void {
    this.createFormFlow.openCreateFormModal();
  }

  openForm(form: FormListItemResponse): void {
    void this.router.navigate(['/forms/builder', form.slug]);
  }

  viewFormData(form: FormListItemResponse): void {
    void this.router.navigate(['/forms', form.slug, 'data']);
  }

  shareForm(form: FormListItemResponse): void {
    void this.router.navigate(['/forms/published', form.slug]);
  }

  getSubmissionCount(_form: FormListItemResponse): number {
    return 24;
  }

  getStatusClass(status: FormListItemResponseStatus): string {
    switch (status) {
      case FormStatusEnum.published:
        return 'bg-emerald-50 text-emerald-700';
      case FormStatusEnum.draft:
        return 'bg-amber-50 text-amber-700';
      case FormStatusEnum.unpublished:
        return 'bg-slate-100 text-slate-600';
      case FormStatusEnum.finished:
        return 'bg-neutral-100 text-neutral-600';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  }

  getStatusIcon(status: FormListItemResponseStatus): string {
    switch (status) {
      case FormStatusEnum.published:
        return 'assets/icons/check-circle-outline.svg';
      case FormStatusEnum.draft:
        return 'assets/icons/square-edit-outline.svg';
      case FormStatusEnum.unpublished:
        return 'assets/icons/eye-off-outline.svg';
      case FormStatusEnum.finished:
        return 'assets/icons/check-circle-outline.svg';
      default:
        return 'assets/icons/file-document-outline.svg';
    }
  }

  private loadForms(): void {
    this.executeRequest({
      state: this.listState,
      request: this.userAPIService.listForms(),
      handleError: true,
      onSuccess: (response) => {
        this.forms.set(response.data.forms ?? []);
      },
    });
  }
}
